import { Actions } from '@modules/Nzo.CMS/Frontend/libraries/shared/actions/actions.state'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { cn } from '@shared/Frontend/utils/addToClassName'
import React from 'react'
import { BlockActions } from '../shared/blockActions'
import { BlockHeader } from '../shared/blockHeader'
import { BlocksView } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.template'
import { Teaser } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/teasers/teasers.state'
import { TeaserGridItem, TeaserItem, TeaserRelatedItem } from '../../teasers/teaser'
import { AsyncState } from 'ballerina-core'
import { getSite } from '@shared/Frontend/utils/site'
import { translate } from "@shared/Frontend/utils/translate"

const site = getSite()

export const RecentContentBlock: BlocksView["RecentContentBlock"] = ({
  id,
  linkedContent,
  label,
  title,
  introduction,
  primaryAction,
  secondaryAction,
  showActionAsLink,
  items,
}) => {
  const { inView, ref } = useInView({ threshold: 0.1 })
  const teasers = AsyncState.Operations.hasValue(items) ? items.value : undefined

  switch (linkedContent) {
    case 'news':
      return (
        <RecentContentBlockLayout.Content
          id={id}
          ref={ref}
          inView={inView}
          label={label}
          title={title}
          introduction={introduction}
          primaryAction={primaryAction!}
          secondaryAction={secondaryAction!}
          showActionAsLink={showActionAsLink}
        >
          {teasers?.length ? (
            <ul className={site == 'ZEG' ? "grid gridsys--grid block__list" : "teasers"}>
              {teasers.map((item, i) => (
                <RecentContentBlockLayout.Teaser
                  key={i}
                  id={item.id}
                  kind={item.kind}
                  link={item.link}
                  title={item.title}
                  intro={item.intro}
                  image={item.image}
                  pictureSources={item.pictureSources}
                  publicationDate={item.publicationDate}
                />
              ))}
            </ul>
          ) : (
            <div className="block__empty">{translate('common:general:noResultsFound', {}, '')}</div>
          )}
        </RecentContentBlockLayout.Content>
      )
    case 'our-other-views':
      return (
        <RecentContentBlockLayout.RelatedContent
          id={id}
          ref={ref}
          inView={inView}
          label={label}
          title={title}
          introduction={introduction}
          primaryAction={primaryAction!}
          secondaryAction={secondaryAction!}
          showActionAsLink={showActionAsLink}
        >
          {teasers?.length ? (
            <ul className='grid gridsys--grid block__list'>
              {teasers
                .filter(item => item.link.url !== window.location.pathname)
                .map((item, i) => (
                  <RecentContentBlockLayout.RelatedTeaser
                    key={i}
                    id={item.id}
                    kind={item.kind}
                    title={item.title}
                    image={item.image}
                    pictureSources={item.pictureSources}
                    link={item.link}
                  />
                ))}
            </ul>
          ) : (
            <div className="block__empty">{translate('common:general:noResultsFound', {}, '')}</div>
          )}
        </RecentContentBlockLayout.RelatedContent>
      )
    default:
      return <></>;
  }
}

type ContentProps = {
  id: string,
  ref: React.RefObject<HTMLElement>,
  inView: boolean,
  label?: string;
  title?: string;
  introduction?: string;
  children?: React.ReactNode
}

export const RecentContentBlockLayout = {
  Content: (props: ContentProps & Actions) => {
    const { id, ref, inView, children } = props
    return (
      <>
      {site == 'ZEG' ? (
        <section id={id} ref={ref} className={cn(`block block--grid block--recent-news ls-spacing`, inView && 'inview')}>
          <div className="block__inner ls-container ls-gutter">
            <BlockHeader {...props} />

            <div className="block__content--full">
              {children}
            </div>

            <BlockActions {...props} datalayerCategory="grid_block" />
          </div>
        </section>
      ) : (
        <section id={id} ref={ref} className={cn('block block--recent-news ls-spacing', inView && 'inview')}>
          <div className={'block__inner ls-container ls-gutter gridsys--columns'}>
            <div className='gridsys--50p'>
              <BlockHeader {...props} />
              <BlockActions {...props} datalayerCategory={`recentnews_block`} />
            </div>

            <div className="block__content gridsys--50p">{children}</div>
          </div>
        </section>
      )}
      </>
    )
  },
  RelatedContent: (props: ContentProps & Actions) => {
    const { id, ref, inView, children } = props
    return (
      <section id={id} ref={ref} className={cn(`block block--recent-related ls-spacing`, inView && 'inview')}>
        <div className={'block__inner ls-container ls-gutter'}>
          <BlockHeader {...props} />
          <BlockActions {...props} datalayerCategory={`recentrelated_block`} />

          <div className="block__content--full">{children}</div>
        </div>
      </section>
    )
  },
  Teaser: (teaser: Teaser) => {
    if (site === 'ZEG') {
      return <TeaserGridItem {...teaser} showType={false} />
    }

    return <TeaserItem {...teaser} showType={false} />
  },
  RelatedTeaser: (teaser: Teaser) => (
    <TeaserRelatedItem clickableLabel={translate('common:general:readMore', {}, '')} {...teaser} />
  ),
}

